import { JSX, useState } from 'react'
import { createPortal } from 'react-dom'
import { ToolTip } from '../../../../components/toolTip/ToolTip'
import { translations } from '../../../../configs/translations'
import { Language } from '../../../../context/LanguageContext'
import { VehicleSpecData } from '../VehicleSpec.types'
import { QuickVehicleSpecItem } from './items/QuickVehicleSpecItem'
import './QuickVehicleSpec.css'
import { QuickVehicleSpecTitle } from './QuickVehicleSpecTitle/QuickVehicleSpecTitle'

export interface QuickVehicleSpecProps {
	vehicleSpec: VehicleSpecData
}

export const QuickVehicleSpec = ({ vehicleSpec }: QuickVehicleSpecProps) => {

	const [language, setLanguage] = useState<Language>('ru')
	const t = translations[language]

	const [mouseX, setMouseX] = useState(0)
	const [mouseY, setMouseY] = useState(0)
	const [tooltipText, setToolTipText] = useState<JSX.Element | null>(null)

	const handleHover = (event: React.MouseEvent, id: string) => {
		setMouseX(event.clientX)
		setMouseY(event.clientY)
		setToolTipText(
			<p className='quick-vehicle-spec-tooltip-text'>{t[`spec_info_${id}` as keyof typeof translations.ru]}</p>
		)
	}

	const getActualValue = (value: number | string) => {
		if (typeof value === 'string') return value
		return Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(0)
	}

	const handleLeave = () => setToolTipText(null)

	return (
		<>
			<div className='quick-vehicle-specification'>

				<QuickVehicleSpecTitle
					brand={vehicleSpec.vehicle.brand}
					name={vehicleSpec.vehicle.name}
					rank={vehicleSpec.vehicle.rank}
				/>

				<div className='quick-vehicle-spec-content'>
					<QuickVehicleSpecItem
						id='power'
						title={t.power}
						value={`${getActualValue(vehicleSpec.quickInfo.power)} ${t.horse_powers_shortened}`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'power')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='speedometer'
						title={t.speed}
						value={`${getActualValue(vehicleSpec.quickInfo.topSpeed)} км/ч`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'speedometer')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='stopwatch'
						title={t.acceleration_0_100}
						value={`${getActualValue(vehicleSpec.quickInfo.acceleration)} сек.`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'stopwatch')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='weight'
						title={t.weight}
						value={`${getActualValue(vehicleSpec.quickInfo.weight)} кг`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'weight')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='brake'
						title={t.brakes}
						value={`${getActualValue(vehicleSpec.quickInfo.brakesPath)} м`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'brake')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='radius'
						title={t.rotation_radius}
						value={`${getActualValue(vehicleSpec.quickInfo.rotationRadius)} м`}
						onHover={(event: React.MouseEvent) => handleHover(event, 'radius')}
						onLeave={handleLeave}
					/>

					<QuickVehicleSpecItem
						id='drive_bias'
						title={t.drive_bias}
						value={getActualValue(vehicleSpec.quickInfo.driveBias)}
						onHover={(event: React.MouseEvent) => handleHover(event, 'drive_bias')}
						onLeave={handleLeave}
					/>
				</div>

			</div>

			{createPortal(
				<ToolTip
					style='top'
					position={{ x: mouseX, y: mouseY }}
					content={tooltipText ?? ''}
					isShown={tooltipText !== null}
				/>,
				document.body
			)}
		</>
	)
}
